/**
 * La file d'envoi : ce qui reste à faire partir, et ce qu'on en dit quand elle
 * ne se vide pas.
 *
 * Rien ici ne touche au réseau, au stockage ni à l'authentification. C'est ce
 * qui permet au service worker d'en dépendre sans embarquer MSAL — il lit la
 * file et parle, il n'envoie pas (voir
 * `docs/adr/0001-le-service-worker-reveille-mais-n-envoie-pas.md`) — et c'est
 * ce qui permet de l'éprouver seule, sans base ni compte.
 */

import { Photo, SyncStatus } from "../types/Photo";

/**
 * Ce qui reste à faire partir. La file ne connaît pas les produits : une photo
 * en échec d'avant-hier y côtoie celle qu'on vient de prendre.
 *
 * `SYNCING` en fait partie : un envoi ne vit que dans un tour de file, et deux
 * tours ne se chevauchent pas. Une photo trouvée dans cet état est donc
 * l'orpheline d'une session fermée en plein envoi — sans cela, elle resterait à
 * l'écran pour toujours sans qu'aucun réveil ne la reprenne.
 *
 * `CAPTURED` n'en fait pas partie : une photo qui n'a pas encore été enfilée
 * n'est reprise par aucun drainage, et la compter promettrait un envoi que
 * personne ne ferait.
 */
export const isQueued = (photo: Photo): boolean =>
  photo.syncStatus === SyncStatus.PENDING ||
  photo.syncStatus === SyncStatus.FAILED ||
  photo.syncStatus === SyncStatus.SYNCING;

/** Ce que la notification affiche quand la file ne s'est pas vidée. */
export interface QueueNotification {
  title: string;
  body: string;
}

/**
 * Faut-il prévenir l'opérateur, et que lui dire ?
 *
 * `null` quand tout est parti : une notification qui s'affiche pour ne rien
 * annoncer apprend à ignorer les notifications. Sinon elle dit **combien** de
 * photos restent — c'est le seul chiffre qui permette de décider s'il faut
 * rouvrir l'application tout de suite ou en fin de journée.
 */
export const queueNotification = (photos: Photo[]): QueueNotification | null => {
  const remaining = photos.filter(isQueued).length;
  if (remaining === 0) return null;

  return {
    title: "Photos non envoyées",
    body:
      remaining === 1
        ? "1 photo est encore sur le téléphone. Ouvrir l'application pour l'envoyer."
        : `${remaining} photos sont encore sur le téléphone. Ouvrir l'application pour les envoyer.`,
  };
};
